"use client";

import { yupResolver } from "@hookform/resolvers/yup";
import type React from "react";
import { Controller, useForm } from "react-hook-form";
import {
  type AddQuoteResult,
  type ChildEntryPage,
  type ChildQuote,
  type PolicyHolder,
  ProductType,
  type WebShopPublicUser,
} from "@/api_client";
import { ProductContainer } from "@/components/shared/productContainer/ProductContainer";
import { SsnInput } from "@/components/shared/ssnInput/SsnInput";
import { useEntry } from "@/hooks/useEntry";
import {
  type ChildEntryFormType,
  ResolveChildEntry,
} from "@/validators/products/child/ChildEntryValidator";

export type EntryProps = {
  externalUser?: WebShopPublicUser;
  externalPolicyHolder?: PolicyHolder | null;
  externalContent?: ChildEntryPage | null;
  externalIsLoading?: boolean;
  externalSubmitHandler?: (quote: ChildQuote) => Promise<AddQuoteResult>;
};

export const ChildEntryForm = ({
  externalUser,
  externalSubmitHandler,
  externalIsLoading,
  externalContent,
  externalPolicyHolder,
}: EntryProps): React.JSX.Element => {
  const {
    currentUser,
    currentPolicyHolder,
    content,
    isLoading,
    submitHandler,
  } = useEntry<ChildQuote, ChildEntryPage>({
    externalUser,
    externalSubmitHandler,
    externalIsLoading,
    externalContent,
    externalPolicyHolder,
    contentKey: "childEntryPage",
  });

  const defaultValues: ChildQuote = {
    policyHolderSsn:
      currentPolicyHolder?.prefilledSsn ?? currentUser?.ssn ?? "",
    productType: ProductType.CHILD,
    coveredPerson: {
      ssn: "",
    },
    guardianInfo: {
      ssn: "",
    },
  };

  const { handleSubmit, control, formState } = useForm<ChildEntryFormType>({
    defaultValues,
    disabled: isLoading,
    resolver: yupResolver(ResolveChildEntry(content)),
  });
  const { errors } = formState;

  return (
    <ProductContainer>
      <ProductContainer.Entry>
        <ProductContainer.Form
          noValidate
          onSubmit={handleSubmit(submitHandler)}
        >
          <Controller
            control={control}
            name="policyHolderSsn"
            render={({ field }) => (
              <SsnInput
                id={field.name}
                disabled={
                  currentPolicyHolder?.ssnLocked ||
                  currentUser?.isAuthenticated ||
                  isLoading
                }
                placeholder={
                  content?.policyHolderSsn?.placeholder?.value ?? undefined
                }
                popoverMessage={content?.policyHolderSsn?.popover?.value}
                errorMessage={errors.policyHolderSsn?.message}
                {...field}
              >
                {content?.policyHolderSsn?.label?.value}
              </SsnInput>
            )}
          />
          <Controller
            control={control}
            name="coveredPerson.ssn"
            render={({ field }) => (
              <SsnInput
                id={field.name}
                placeholder={content?.childSsn?.placeholder?.value ?? undefined}
                popoverMessage={content?.childSsn?.popover?.value}
                errorMessage={errors.coveredPerson?.ssn?.message}
                {...field}
              >
                {content?.childSsn?.label?.value}
              </SsnInput>
            )}
          />
          <ProductContainer.CallToAction htmlType="submit" loading={isLoading}>
            {content?.submitButton?.label?.value}
          </ProductContainer.CallToAction>
        </ProductContainer.Form>
      </ProductContainer.Entry>
    </ProductContainer>
  );
};
